import { SupabaseClient } from "@supabase/supabase-js";
import {
  ChangeProposalRepository,
  ChangeProposalDocumentRepository,
} from "../repositories/change-proposal.repository";
import { ProjectUserRepository } from "../repositories/project-user.repository";
import {
  ChangeProposalDTO,
  ChangeProposalDocumentDTO,
  CreateChangeProposalDTO,
  UpdateChangeProposalDTO,
  ChangeProposalWithDocumentsDTO,
  ApproveChangeProposalResultDTO,
} from "../dtos/change-proposal.dto";
import { ChangeProposalMapper } from "../mappers/change-proposal.mapper";
import { UserRole } from "../types/user-role.enum";

export class ChangeProposalService {
  private changeProposalRepository: ChangeProposalRepository;
  private changeProposalDocumentRepository: ChangeProposalDocumentRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.changeProposalRepository = new ChangeProposalRepository(supabase);
    this.changeProposalDocumentRepository =
      new ChangeProposalDocumentRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * Create a new change proposal
   */
  async createProposal(
    dto: CreateChangeProposalDTO,
    userId: string,
  ): Promise<ChangeProposalWithDocumentsDTO> {
    // Check user permissions
    await this.checkUserPermissions(dto.projectId, userId, UserRole.MEMBER);

    try {
      // Create the proposal
      const proposalData = {
        project_id: dto.projectId,
        chat_id: dto.chatId,
        title: dto.title,
        description: dto.description,
        status: "pending" as const,
        created_by: userId,
      };

      const proposal = await this.changeProposalRepository.create(proposalData);

      // Create proposal documents
      const documentData = dto.documents.map((doc) => ({
        change_proposal_id: proposal.id,
        document_id: doc.documentId,
        original_content: doc.originalContent,
        proposed_content: doc.proposedContent,
      }));

      const documents =
        await this.changeProposalDocumentRepository.createMultiple(
          documentData,
        );

      return ChangeProposalMapper.toWithDocumentsDTO(proposal, documents);
    } catch (error) {
      throw new Error(`Failed to create change proposal: ${error}`);
    }
  }

  /**
   * Get a change proposal by ID
   */
  async getProposal(
    proposalId: string,
    userId: string,
  ): Promise<ChangeProposalDTO | null> {
    const proposal = await this.changeProposalRepository.findById(proposalId);
    if (!proposal) return null;

    // Check user permissions
    await this.checkUserPermissions(
      proposal.project_id,
      userId,
      UserRole.VIEWER,
    );

    return ChangeProposalMapper.toDTO(proposal);
  }

  /**
   * Get a change proposal with its documents
   */
  async getProposalWithDocuments(
    proposalId: string,
    userId: string,
  ): Promise<ChangeProposalWithDocumentsDTO | null> {
    try {
      console.log(
        "🔍 Service: Getting proposal with documents for ID:",
        proposalId,
      );

      const result =
        await this.changeProposalRepository.findByIdWithDocuments(proposalId);
      console.log("✅ Repository result:", result ? "found" : "null");

      if (!result) return null;

      console.log("🔍 Proposal data:", result.proposal);
      console.log("🔍 Documents count:", result.documents.length);

      // Check user permissions
      await this.checkUserPermissions(
        result.proposal.project_id,
        userId,
        UserRole.VIEWER,
      );
      console.log("✅ Permissions checked");

      const mappedResult = ChangeProposalMapper.toWithDocumentsDTO(
        result.proposal,
        result.documents,
      );
      console.log("✅ Mapper result:", mappedResult);

      return mappedResult;
    } catch (error) {
      console.error("❌ Service error in getProposalWithDocuments:", error);
      console.error("❌ Error stack:", (error as Error).stack);
      throw error;
    }
  }

  /**
   * Get all proposals for a project
   */
  async getProjectProposals(
    projectId: string,
    userId: string,
    status?: "pending" | "approved" | "rejected",
  ): Promise<ChangeProposalDTO[]> {
    // Check user permissions
    await this.checkUserPermissions(projectId, userId, UserRole.VIEWER);

    let proposals;
    if (status) {
      proposals = await this.changeProposalRepository.findByStatus(
        projectId,
        status,
      );
    } else {
      proposals =
        await this.changeProposalRepository.findByProjectId(projectId);
    }

    return proposals.map((proposal) => ChangeProposalMapper.toDTO(proposal));
  }

  /**
   * Get proposals by chat ID
   */
  async getChatProposals(
    chatId: string,
    userId: string,
  ): Promise<ChangeProposalDTO[]> {
    const proposals = await this.changeProposalRepository.findByChatId(chatId);

    if (proposals.length > 0) {
      // Check user permissions for the first proposal's project
      await this.checkUserPermissions(
        proposals[0].project_id,
        userId,
        UserRole.VIEWER,
      );
    }

    return proposals.map((proposal) => ChangeProposalMapper.toDTO(proposal));
  }

  /**
   * Update a change proposal
   */
  async updateProposal(
    proposalId: string,
    dto: UpdateChangeProposalDTO,
    userId: string,
  ): Promise<ChangeProposalDTO> {
    const proposal = await this.changeProposalRepository.findById(proposalId);
    if (!proposal) {
      throw new Error("Change proposal not found");
    }

    // Check user permissions
    await this.checkUserPermissions(
      proposal.project_id,
      userId,
      UserRole.MEMBER,
    );

    // Update proposal
    if (dto.title) proposal.setTitle(dto.title);
    if (dto.description) proposal.setDescription(dto.description);

    if (dto.status && dto.status !== proposal.status) {
      if (dto.status === "approved") {
        proposal.approve(userId);
      } else if (dto.status === "rejected") {
        proposal.reject(userId);
      }
    }

    const updatedProposal = await this.changeProposalRepository.save(proposal);
    return ChangeProposalMapper.toDTO(updatedProposal);
  }

  /**
   * Approve a change proposal
   */
  async approveProposal(
    proposalId: string,
    userId: string,
    selectedDocumentIds?: string[],
  ): Promise<ApproveChangeProposalResultDTO> {
    // Make the API call to the Edge Function
    const { data: session } = await this.supabase.auth.getSession();
    if (!session?.session) {
      throw new Error("No active session");
    }

    // Get Supabase URL from the client instance
    const { supabaseUrl } = this.supabase as any;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/approve-change-proposal`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({
          proposalId,
          selectedDocumentIds,
        }),
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to approve proposal");
    }

    return response.json() as ApproveChangeProposalResultDTO;
  }

  /**
   * Reject a change proposal
   */
  async rejectProposal(
    proposalId: string,
    userId: string,
  ): Promise<ChangeProposalDTO> {
    const proposal = await this.changeProposalRepository.findById(proposalId);
    if (!proposal) {
      throw new Error("Change proposal not found");
    }

    // Check user permissions
    await this.checkUserPermissions(
      proposal.project_id,
      userId,
      UserRole.MEMBER,
    );

    if (!proposal.isPending()) {
      throw new Error("Only pending proposals can be rejected");
    }

    proposal.reject(userId);
    const updatedProposal = await this.changeProposalRepository.save(proposal);

    return ChangeProposalMapper.toDTO(updatedProposal);
  }

  /**
   * Delete a change proposal
   */
  async deleteProposal(proposalId: string, userId: string): Promise<void> {
    const proposal = await this.changeProposalRepository.findById(proposalId);
    if (!proposal) {
      throw new Error("Change proposal not found");
    }

    // Check user permissions (only creator or admin can delete)
    const userRole = await this.checkUserPermissions(
      proposal.project_id,
      userId,
      UserRole.VIEWER,
    );
    if (
      proposal.created_by !== userId &&
      userRole !== UserRole.ADMIN &&
      userRole !== UserRole.OWNER
    ) {
      throw new Error(
        "You can only delete your own proposals or you need admin permissions",
      );
    }

    // Delete proposal documents first (due to foreign key constraint)
    await this.changeProposalDocumentRepository.deleteByProposalId(proposalId);

    // Delete the proposal
    await this.changeProposalRepository.delete(proposalId);
  }

  /**
   * Get documents for a specific proposal
   */
  async getProposalDocuments(
    proposalId: string,
    userId: string,
  ): Promise<ChangeProposalDocumentDTO[]> {
    const proposal = await this.changeProposalRepository.findById(proposalId);
    if (!proposal) {
      throw new Error("Change proposal not found");
    }

    // Check user permissions
    await this.checkUserPermissions(
      proposal.project_id,
      userId,
      UserRole.VIEWER,
    );

    const documents =
      await this.changeProposalDocumentRepository.findByProposalId(proposalId);
    return documents.map((doc) => ChangeProposalMapper.toDocumentDTO(doc));
  }

  /**
   * Check if user has required permissions for the project
   */
  private async checkUserPermissions(
    projectId: string,
    userId: string,
    requiredRole: UserRole,
  ): Promise<UserRole> {
    const projectUser =
      await this.projectUserRepository.findByProjectIdAndUserId(
        projectId,
        userId,
      );

    if (!projectUser) {
      throw new Error("You don't have access to this project");
    }

    const userRole = projectUser.role as UserRole;
    const roleHierarchy = {
      [UserRole.VIEWER]: 1,
      [UserRole.MEMBER]: 2,
      [UserRole.ADMIN]: 3,
      [UserRole.OWNER]: 4,
    };

    if (roleHierarchy[userRole] < roleHierarchy[requiredRole]) {
      throw new Error("Insufficient permissions");
    }

    return userRole;
  }
}

